import { useCallback, useEffect, useRef, useState } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import {
  WHY_CHOOSE_US_AUTO_SLIDE_MS,
  WHY_CHOOSE_US_ITEMS,
  type WhyChooseUsItem,
} from '@/lib/whyChooseUsContent'
import { cn } from '@/lib/utils'
import { HomeSection } from './HomeSection'
import { homeEase, homeFadeUp } from './motion'

function WhyChooseUsCard({ item, variant }: { item: WhyChooseUsItem; variant: 'a' | 'b' }) {
  const Icon = item.icon
  const isA = variant === 'a'

  return (
    <article
      data-why-choose-us-card
      className="group flex w-[min(78vw,260px)] shrink-0 snap-start flex-col sm:w-[280px]"
    >
      <div
        className={cn(
          'gold-gradient-ring h-full p-[2px] shadow-md shadow-gold-500/10 transition-shadow duration-300 group-hover:shadow-lg group-hover:shadow-gold-500/20',
          isA ? 'brand-radius-a-lg' : 'brand-radius-b-lg'
        )}
      >
        <div
          className={cn(
            'flex h-full flex-col border border-gold-500/10 bg-white/90 p-5 backdrop-blur-sm sm:p-6',
            isA ? 'brand-radius-a-lg-inset-2' : 'brand-radius-b-lg-inset-2'
          )}
        >
          <div
            className={cn(
              'mb-4 flex h-12 w-12 items-center justify-center bg-gradient-to-br from-gold-500/15 to-gold-600/5 ring-1 ring-gold-500/20 transition-transform duration-300 group-hover:scale-105',
              isA ? 'brand-radius-a' : 'brand-radius-b'
            )}
          >
            <Icon className="h-6 w-6 text-gold-700" aria-hidden />
          </div>
          <h3 className="font-display text-lg font-semibold text-luxury-950">
            {item.title}
          </h3>
          <p className="mt-2 flex-1 text-sm leading-relaxed text-zinc-600">
            {item.description}
          </p>
        </div>
      </div>
    </article>
  )
}

function getScrollStep(el: HTMLDivElement) {
  const card = el.querySelector<HTMLElement>('[data-why-choose-us-card]')
  if (!card) return 300
  const gap = Number.parseFloat(getComputedStyle(el).columnGap || getComputedStyle(el).gap) || 16
  return card.offsetWidth + gap
}

function WhyChooseUsScroller({ items }: { items: WhyChooseUsItem[] }) {
  const reduceMotion = useReducedMotion()
  const scrollRef = useRef<HTMLDivElement>(null)
  const directionRef = useRef<1 | -1>(1)
  const [edges, setEdges] = useState({ left: false, right: false })
  const [paused, setPaused] = useState(false)

  const updateEdges = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    setEdges({
      left: el.scrollLeft > 12,
      right: el.scrollLeft < el.scrollWidth - el.clientWidth - 12,
    })
  }, [])

  const autoScrollStep = useCallback(() => {
    const el = scrollRef.current
    if (!el) return

    const step = getScrollStep(el)
    const maxScroll = Math.max(0, el.scrollWidth - el.clientWidth)
    const behavior = reduceMotion ? 'auto' : 'smooth'
    const atEnd = el.scrollLeft >= maxScroll - 8
    const atStart = el.scrollLeft <= 8

    if (directionRef.current === 1) {
      if (atEnd) {
        directionRef.current = -1
        el.scrollBy({ left: -step, behavior })
      } else {
        el.scrollBy({ left: step, behavior })
      }
    } else if (atStart) {
      directionRef.current = 1
      el.scrollBy({ left: step, behavior })
    } else {
      el.scrollBy({ left: -step, behavior })
    }
  }, [reduceMotion])

  const scrollBy = (direction: 'left' | 'right') => {
    const el = scrollRef.current
    if (!el) return

    directionRef.current = direction === 'left' ? -1 : 1
    el.scrollBy({
      left: direction === 'left' ? -getScrollStep(el) : getScrollStep(el),
      behavior: reduceMotion ? 'auto' : 'smooth',
    })
  }

  useEffect(() => {
    updateEdges()
    const el = scrollRef.current
    if (!el) return
    const ro = new ResizeObserver(updateEdges)
    ro.observe(el)
    return () => ro.disconnect()
  }, [items.length, updateEdges])

  useEffect(() => {
    if (items.length <= 1 || paused || reduceMotion) return

    const timer = window.setInterval(autoScrollStep, WHY_CHOOSE_US_AUTO_SLIDE_MS)
    return () => window.clearInterval(timer)
  }, [items.length, paused, reduceMotion, autoScrollStep])

  const showControls = items.length > 1

  return (
    <div
      className="relative sm:-mx-6 lg:-mx-8"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          setPaused(false)
        }
      }}
    >
      {showControls && edges.left && (
        <button
          type="button"
          onClick={() => scrollBy('left')}
          className="absolute left-2 top-1/2 z-20 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-gold-500/30 bg-white/95 text-luxury-900 shadow-lg backdrop-blur-sm transition hover:bg-gold-50 focus-ring md:flex"
          aria-label="Scroll reasons left"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
      )}
      {showControls && edges.right && (
        <button
          type="button"
          onClick={() => scrollBy('right')}
          className="absolute right-2 top-1/2 z-20 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-gold-500/30 bg-white/95 text-luxury-900 shadow-lg backdrop-blur-sm transition hover:bg-gold-50 focus-ring md:flex"
          aria-label="Scroll reasons right"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      )}

      <div
        className={cn(
          'pointer-events-none absolute inset-y-0 left-0 z-10 w-8 bg-gradient-to-r from-[#FAF8F4] to-transparent transition-opacity duration-300 sm:left-6 lg:left-8',
          edges.left ? 'opacity-100' : 'opacity-0'
        )}
        aria-hidden
      />
      <div
        className={cn(
          'pointer-events-none absolute inset-y-0 right-0 z-10 w-8 bg-gradient-to-l from-[#F3EFE8] to-transparent transition-opacity duration-300 sm:right-6 lg:right-8',
          edges.right ? 'opacity-100' : 'opacity-0'
        )}
        aria-hidden
      />

      <div
        ref={scrollRef}
        onScroll={updateEdges}
        className={cn(
          'flex gap-4 overflow-x-auto pb-2 pt-1 sm:gap-5 sm:px-6 lg:px-8',
          'snap-x snap-mandatory scroll-smooth',
          '[scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden'
        )}
      >
        {items.map((item, index) => (
          <WhyChooseUsCard
            key={item.id}
            item={item}
            variant={index % 2 === 0 ? 'a' : 'b'}
          />
        ))}
      </div>
    </div>
  )
}

export function HomeWhyChooseUs() {
  const reduceMotion = useReducedMotion()

  return (
    <HomeSection
      id="why-choose-us"
      eyebrow="The Ooudh Kraft difference"
      title="Why Choose Us"
      subtitle="Every bottle reflects our commitment to purity, longevity, and craftsmanship — the qualities that set our attars apart."
      align="center"
      size="compact"
      className="relative overflow-hidden bg-gradient-to-b from-[#FAF8F4] via-white to-[#F3EFE8]"
      headerClassName="mx-auto max-w-2xl text-center"
    >
      <motion.div
        initial={reduceMotion ? false : 'hidden'}
        whileInView="visible"
        viewport={{ once: true, margin: '-60px' }}
        variants={homeFadeUp}
        transition={{ duration: 0.5, ease: homeEase }}
      >
        <WhyChooseUsScroller items={WHY_CHOOSE_US_ITEMS} />
      </motion.div>
    </HomeSection>
  )
}
